import { SosOrderStatus } from "./SosOrder";

/**
 * Перечисление ролей пользователей
 * @enum {string} ADULT - Взрослый
 * @enum {string} CHILD - Ребенок
 * @enum {string} ADMIN - Админ
 * @enum {string} DISPATCHER - Диспетчер
 */
export enum UserRole {
  ADULT = "ADULT",
  CHILD = "CHILD",
  ADMIN = "ADMIN",
  DISPATCHER = "DISPATCHER",
}

/**
 * Отображаемый текст для ролей пользователей
 * @enum {string} ADULT - Взрослый
 * @enum {string} CHILD - Ребенок
 * @enum {string} ADMIN - Админ
 * @enum {string} DISPATCHER - Диспетчер
 */
export enum UserRoleDisplayText {
  ADULT = "Взрослый",
  CHILD = "Ребенок",
  ADMIN = "Админ",
  DISPATCHER = "Диспетчер",
}

/**
 * Статусы пользователей, правила обновления по порядку, то есть awaiting - approved - blocked
 * @enum {string} AWAITING - На проверке
 * @enum {string} APPROVED - Подтвержден
 * @enum {string} BLOCKED - Заблокирован
 */
export enum UserStatus {
  AWAITING = "AWAITING",
  APPROVED = "APPROVED",
  BLOCKED = "BLOCKED",
}

/**
 * Отображаемый текст для статуса пользователя
 * @enum {string} AWAITING - На проверке
 * @enum {string} APPROVED - Подтвержден
 * @enum {string} BLOCKED - Заблокирован
 */
export enum UserStatusDisplayText {
  AWAITING = "На проверке",
  APPROVED = "Подтвержден",
  BLOCKED = "Заблокирован",
}

/**
 * Тело запроса для смены статуса пользователя
 */
export interface UserChangeStatusRequestData {
  status: UserStatus;
  reason: string;
}

/**
 * Данные для создания взрослого пользователя из админской панели.
 */
export interface AdultUserCreateRequestData {
  phone_number: string;
  name: string;
  iin?: string | null;
}

/**
 * Формат координат местоположения пользователя
 */
export interface LocationCoordinates {
  latitude: number;
  longitude: number;
}

/**
 * Ответ сервера на запрос получения актуальных координат местоположения пользователя
 */
export interface GetCurrentLocationResponseData extends LocationCoordinates {
  created_on: Date;
}

/**
 * Интерфейс ответа API с пользователем
 */
export interface UserResponse {
  /** Код ответа API */
  api_code: string;
  /** Сообщение ответа API */
  api_message: string;
  /** Данные о пользователе */
  data: UserResponseData;
}

/**
 * Интерфейс пользователя
 */
export interface UserFromSosOrder {
  /** Уникальный идентификатор пользователя */
  id: number;
  /** Имя пользователя */
  name: string;
  /** Номер телефона */
  phone_number: string;
  /** Роль пользователя */
  role_name: UserRole;
  /** Номер телефона владельца семьи (если есть) */
  family_owner_phone_number: string | null;
  /** Тип подписки пользователя (если есть) */
  subscription_type: UserSubscriptionType | null;
  /** Дата создания пользователя */
  date_create: Date;
}

/**
 * Перечисление типов подписки пользователей
 * @enum {string} INDIVIDUAL - Индивидуальный
 * @enum {string} FAMILY - Семейный (По логике равносильна TRIAL)
 * @enum {string} TRIAL - Пробный (По логике равносильна FAMILY)
 */
export enum UserSubscriptionType {
  INDIVIDUAL = "INDIVIDUAL",
  FAMILY = "FAMILY", // По логике равносильна TRIAL
  TRIAL = "TRIAL", // По логике равносильна FAMILY
}

/**
 * Отображаемый текст для типов подписки
 * @enum {string} INDIVIDUAL - Индивидуальный
 * @enum {string} FAMILY - Семейный (По логике равносильна TRIAL)
 * @enum {string} TRIAL - Пробный (По логике равносильна FAMILY)
 */
export enum UserSubscriptionTypeDisplayText {
  INDIVIDUAL = "Индивидуальная",
  FAMILY = "Семейная", // По логике равносильна TRIAL
  TRIAL = "Пробная", // По логике равносильна FAMILY
}

/**
 * Интерфейс ответа API на получение всех Пользователей.
 *
 * Используется для обработки списка Пользователей, возвращаемых сервером.
 */
export interface AllUsersResponse {
  /** Код ответа API */
  api_code: string;

  /** Сообщение ответа API */
  api_message_: string;

  /** Массив данных с Пользователями */
  data: UserResponseData[];

  /** Количество всего страниц */
  pages: number;

  /** Количество всего записей */
  total: number;
}

/**
 * Упрощённый интерфейс пользователя без полей `family` и `contacts`.
 *
 * Используется для вложенных структур внутри пользователя, чтобы избежать рекурсии.
 *
 * См. также {@link UserResponseData} — полная версия с вложенностями.
 */
export interface UserFlatData {
  /** Уникальный идентификатор пользователя */
  id: number;

  /** Возраст пользователя */
  age: number | null;

  /** Признак, является ли пользователь владельцем семьи */
  is_family_owner: boolean;

  /** Признак, является ли пользователь участником семьи */
  has_family: boolean;

  /** Имя пользователя */
  name: string;

  /** Номер телефона */
  phone_number: string;

  /** Роль пользователя */
  role: UserRole;

  /** Список подписок пользователя */
  subscriptions: UserSubscription[] | null;

  /** Тип устройства пользователя */
  device_type: string;

  /** Тип устройства пользователя */
  status: UserStatus;

  /** Дата создания пользователя */
  date_create: Date;
}

/**
 * Интерфейс данных отдельного пользователя.
 *
 * Включает вложенные данные о членах семьи и контактах пользователя.
 */
export interface UserResponseData extends UserFlatData {
  /** Члены семьи пользователя */
  family: UserFlatData[];

  /** Контакты пользователя */
  contacts: UserFlatData[];

  /** История заявок пользователя */
  requisitions: UserOrderHistoryItem[];
}

export interface UserOrderHistoryItem {
  address: string;
  date_create: string;
  id: number;
  latitude: number;
  longitude: number;
  status_actual: SosOrderStatus;
}

/**
 * Подписка пользователя.
 *
 * Содержит информацию о типе подписки, сроках её действия и статусе (активна или нет).
 */
export interface UserSubscription {
  /** Дата начала действия подписки */
  date_start: string;

  /** Дата окончания действия подписки */
  date_end: string;

  /** Тип подписки пользователя */
  type: UserSubscriptionType;

  /** Признак, истекла ли подписка */
  expired: boolean;
}

/**
 * Данные для обновления подписки у пользователя
 */
export interface UserSubscriptionPostData {
  /** Тип подписки пользователя */
  subscription_type: UserSubscriptionType;
  /** Дата окончания действия подписки */
  date_end: string;
}
